import React from "react";
import type { VoucherDailyAnalytics } from "../../services/voucherInstances.service";

interface Props {
  data: VoucherDailyAnalytics[];
  loading?: boolean;
}

const VoucherAnalyticsChart: React.FC<Props> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Voucher Value Analytics
        </h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No data available for the selected filters
        </div>
      </div>
    );
  }

  // Calculate max value for scaling
  const maxValue = Math.max(
    ...data.map((d) => Math.max(d.issued_value, d.redeemed_value)),
  );

  // Calculate totals
  const totalIssued = data.reduce((sum, d) => sum + d.issued_value, 0);
  const totalRedeemed = data.reduce((sum, d) => sum + d.redeemed_value, 0);
  const totalIssuedCount = data.reduce((sum, d) => sum + d.issued_count, 0);
  const totalRedeemedCount = data.reduce((sum, d) => sum + d.redeemed_count, 0);

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Calculate bar heights (max height 200px)
  const getBarHeight = (value: number) => {
    if (maxValue === 0) {
      return 0;
    }
    return (value / maxValue) * 200;
  };

  // Limit to last 30 days if more data
  const displayData = data.slice(-30);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Voucher Value Analytics (by Day)
        </h3>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-primary-50 rounded-lg p-4">
            <div className="text-sm text-primary-600 font-medium">
              Total Issued
            </div>
            <div className="text-2xl font-bold text-primary-900">
              $
              {totalIssued.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="text-xs text-primary-600 mt-1">
              {totalIssuedCount} vouchers
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">
              Total Redeemed
            </div>
            <div className="text-2xl font-bold text-green-900">
              $
              {totalRedeemed.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="text-xs text-green-600 mt-1">
              {totalRedeemedCount} vouchers
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-purple-600 font-medium">
              Redemption Rate
            </div>
            <div className="text-2xl font-bold text-purple-900">
              {totalIssuedCount > 0
                ? ((totalRedeemedCount / totalIssuedCount) * 100).toFixed(1)
                : "0.0"}
              %
            </div>
            <div className="text-xs text-purple-600 mt-1">by count</div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-sm text-orange-600 font-medium">
              Outstanding
            </div>
            <div className="text-2xl font-bold text-orange-900">
              $
              {(totalIssued - totalRedeemed).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="text-xs text-orange-600 mt-1">not yet redeemed</div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-primary-500 rounded"></div>
            <span className="text-sm text-gray-600">Issued Value</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-sm text-gray-600">Redeemed Value</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative">
        <div className="flex items-end justify-start gap-2 overflow-x-auto pb-4">
          {displayData.map((day) => {
            const issuedHeight = getBarHeight(day.issued_value);
            const redeemedHeight = getBarHeight(day.redeemed_value);

            return (
              <div
                key={day.date}
                className="flex-shrink-0 flex flex-col items-center"
              >
                {/* Bars */}
                <div className="flex items-end gap-1 h-52">
                  {/* Issued bar */}
                  <div
                    className="w-8 bg-primary-500 hover:bg-primary-600 transition-colors rounded-t relative group cursor-pointer"
                    style={{
                      height: `${issuedHeight}px`,
                      minHeight: day.issued_value > 0 ? "4px" : "0",
                    }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                      <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                        <div className="font-medium">Issued</div>
                        <div>${day.issued_value.toFixed(2)}</div>
                        <div className="text-gray-300">
                          {day.issued_count} vouchers
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Redeemed bar */}
                  <div
                    className="w-8 bg-green-500 hover:bg-green-600 transition-colors rounded-t relative group cursor-pointer"
                    style={{
                      height: `${redeemedHeight}px`,
                      minHeight: day.redeemed_value > 0 ? "4px" : "0",
                    }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                      <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                        <div className="font-medium">Redeemed</div>
                        <div>${day.redeemed_value.toFixed(2)}</div>
                        <div className="text-gray-300">
                          {day.redeemed_count} vouchers
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date label */}
                <div className="text-xs text-gray-600 mt-2 transform -rotate-45 origin-top-left whitespace-nowrap">
                  {formatDate(day.date)}
                </div>
              </div>
            );
          })}
        </div>

        {data.length > 30 && (
          <div className="text-xs text-gray-500 text-center mt-4">
            Showing last 30 days of data
          </div>
        )}
      </div>
    </div>
  );
};

export default VoucherAnalyticsChart;
